import { useEffect, useRef } from 'react';
import { ArrowRight, Users, Wrench, Zap } from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';
import VideoComponent from './Video';
import LogoCarousel from './LogoCarousel';

const statsData = [
  {
    number: '+ de 50.000',
    label: 'Advogados confiam',
    icon: Users,
    delay: 0.2,
  },
  {
    number: '+ de 35',
    label: 'Ferramentas',
    icon: Wrench,
    delay: 0.4,
  },
  {
    number: '11x',
    label: 'Mais produtividade',
    icon: Zap,
    delay: 0.6,
  },
];

export default function Hero() {
  const statsRef = useRef(null);
  const isInView = useInView(statsRef, { once: true, margin: '-100px' });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  useEffect(() => {
    const urlParams = window.location.search; // Get URL parameters
    const button = document.getElementById('ctaButton') as HTMLAnchorElement;

    if (urlParams && button) {
      // Remove the leading '?' from urlParams if it exists
      const cleanParams = urlParams.startsWith('?') ? urlParams.substring(1) : urlParams;
      
      // Create a new URL object to handle the parameters properly
      const buttonUrl = new URL(button.href);
      // Set the new search parameters
      buttonUrl.search = cleanParams;
      // Update the button href with the properly formatted URL
      button.href = buttonUrl.toString();
    }
  }, []);

  return (
    <div className="relative min-h-screen flex items-center pt-12" style={{ overflow: 'visible' }}>
      {/* Background gradient effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#6942E2] opacity-20 blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-[#28E7C5] opacity-20 blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative container mx-auto px-6 py-16 md:py-16" style={{ overflow: 'visible' }}>
        <div className="max-w-4xl mx-auto text-center" style={{ overflow: 'visible' }}>
          <h1
            className="text-4xl md:text-6xl font-bold mb-16 
                       bg-gradient-to-r from-white to-gray-400 
                       bg-clip-text text-transparent inline-block 
                       leading-[1.2] py-2"
            style={{
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              overflow: 'visible',
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            }}
          >
            A melhor IA para advogados
          </h1>

          <div className="relative w-full aspect-video mb-8 rounded-xl overflow-hidden backdrop-blur-lg bg-white/5 border border-white/10">
            <div className="absolute inset-0 flex items-center justify-center">
              <VideoComponent />
            </div>
          </div>

          <p className="text-xl md:text-2xl mb-8 text-gray-300">
            Crie peças, estratégias e faça análises com uma IA jurídica 100% treinada na legislação, doutrina e jurisprudência brasileiras
          </p>

          {/* CTA Buttons with shining effect */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
                id="ctaButton"
                href="https://plataforma.lawx.ai/dashboard" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group"
              >
                <button
                  className="group relative px-8 py-4 
                             bg-gradient-to-r from-[#4FC3F7] to-[#0B63F8] 
                             text-white rounded-lg font-semibold text-lg 
                             transition-all duration-300 
                             hover:shadow-[0_0_20px_rgba(11,99,248,0.4)] 
                             hover:scale-105 overflow-hidden w-full sm:w-auto"
                >
                  Teste grátis!
                  <ArrowRight className="inline-block ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent 
                               opacity-0 group-hover:opacity-100 
                               transform -translate-x-full skew-x-12 
                               group-hover:translate-x-full 
                               transition-transform duration-700 ease-in-out pointer-events-none"
                  />
                </button>
              </a>

              <a 
                href="https://api.whatsapp.com/send/?phone=558197225383&text=Ol%C3%A1%21+Quero+saber+mais+sobre+as+ferramentas+personalizadas%21&type=phone_number&app_absent=0" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group"
              >
                <button
                  className="group relative px-8 py-4 
                             bg-gradient-to-r from-[#25D366] to-[#128C7E] 
                             text-white rounded-lg font-semibold text-lg 
                             transition-all duration-300 
                             hover:shadow-[0_0_20px_rgba(37,211,102,0.4)] 
                             hover:scale-105 overflow-hidden w-full sm:w-auto"
                >
                  Solicitar ferramenta personalizada
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent 
                               opacity-0 group-hover:opacity-100 
                               transform -translate-x-full skew-x-12 
                               group-hover:translate-x-full 
                               transition-transform duration-700 ease-in-out pointer-events-none"
                  />
                </button>
              </a>
          </div>
            <LogoCarousel />

          {/* Stats with glass effect */}
          <div ref={statsRef} className="mt-8 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {statsData.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={controls}
                  variants={{
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        delay: stat.delay,
                        ease: 'easeOut',
                      },
                    },
                  }}
                  transition={{
                    duration: 0.5,
                    delay: stat.delay,
                    ease: 'easeOut',
                  }}
                  className="group backdrop-blur-lg bg-white/10 rounded-lg p-6 border border-white/20 
                             flex flex-col items-center relative 
                             transition-all duration-500 ease-out
                             hover:transform hover:scale-105 hover:-translate-y-2
                             hover:bg-white/15 hover:border-[#28E7C5]/40
                             hover:shadow-[0_0_30px_rgba(40,231,197,0.2)]
                             before:absolute before:inset-0 
                             before:bg-gradient-to-r before:from-transparent before:via-white/5 before:to-transparent
                             before:opacity-0 before:transition-opacity before:duration-500
                             hover:before:opacity-100"
                >
                  <Icon className="w-8 h-8 mb-4 text-[#2D93F8] transition-transform duration-500 group-hover:scale-110 group-hover:text-[#28E7C5]" />
                  <div className="text-3xl font-bold mb-2 transition-colors duration-500 group-hover:text-white">
                    {stat.number}
                  </div>
                  <div className="text-gray-300 transition-colors duration-500 group-hover:text-[#28E7C5]">
                    {stat.label}
                  </div>
                  <div className="absolute -inset-px bg-gradient-to-r from-[#28E7C5]/0 via-[#28E7C5]/10 to-[#28E7C5]/0 
                                opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
